// RequireAuth.jsx



// RequireAuth.jsx
import React, { useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

/**
 * Usage:
 * <RequireAuth>...protected...</RequireAuth>
 * <RequireAuth roles="renter">...renter only...</RequireAuth>
 * <RequireAuth roles={[ROLES.AGENT, ROLES.MANAGER]}>...agent/manager...</RequireAuth>
 */
const RequireAuth = ({ children, roles = null, fallbackPath = "/", showLoader = true }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Normalize roles prop to an array (or null if no role restriction)
  const roleList = useMemo(() => {
    if (!roles) return null;
    return Array.isArray(roles) ? roles : [roles];
  }, [roles]);

  const isAuthorized = useMemo(() => {
    if (!currentUser) return false;

    if (!roleList || roleList.length === 0) return true; // no role restriction
    return roleList.includes(currentUser.userType);

  }, [currentUser, roleList]);


  useEffect(() => {
    // Not logged in -> go to login and preserve intended destination
    if (!currentUser) {
      navigate("/login", { replace: true, state: { from: location } });
      return;
    }
    // Logged in but wrong role -> bounce to fallback
    if (!isAuthorized) {
      navigate(fallbackPath, { replace: true });
    }
  }, [currentUser, isAuthorized, navigate, location, fallbackPath]);

  // While deciding / redirecting, optionally show a centered loader
  if (!currentUser || (roleList && !isAuthorized)) {
    return showLoader ? (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    ) : null;
  }

  return children;
};

export default RequireAuth;


